import React from "react";
import { Link } from "react-router-dom";
import { Clock, Car, Star, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";



const packages = [
  {
    id: "kannur-sightseeing",
    name: "Kannur Sightseeing",
    duration: "1 Day",
    distance: "~60 km",
    description: "Explore the beautiful beaches and historical monuments of Kannur in one day.",
    gradient: "from-blue-500/10 to-cyan-400/10",
    iconColor: "text-blue-500",
    rating: 4.8,
    image: "/images/packages/kannur.png",
  },
  {
    id: "wayanad-tour",
    name: "Wayanad Tour",
    duration: "2 Days / 1 Night",
    distance: "~280 km",
    description: "Discover the lush green landscapes and adventure spots of Wayanad.",
    gradient: "from-emerald-500/10 to-green-400/10",
    iconColor: "text-emerald-500",
    rating: 4.9,
    image: "/images/packages/wayanad.png",
  },
  {
    id: "coorg-trip",
    name: "Coorg Trip",
    duration: "2 Days / 1 Night",
    distance: "~240 km",
    description: "Experience the Scotland of India with breathtaking views and coffee aromas.",
    gradient: "from-orange-500/10 to-amber-400/10",
    iconColor: "text-orange-500",
    rating: 4.7,
    image: "/images/packages/coorg.png",
  },
  {
    id: "kerala-tour",
    name: "Kerala Tour Package",
    duration: "5 Days / 4 Nights",
    distance: "~800 km",
    description: "A comprehensive Kerala experience covering hill stations, backwaters, and beaches.",
    gradient: "from-purple-500/10 to-pink-400/10",
    iconColor: "text-purple-500",
    rating: 5.0,
    image: "/images/packages/kerala.png",
  },
  {
    id: "mysore-ooty",
    name: "Mysore & Ooty Tour",
    duration: "3 Days / 2 Nights",
    distance: "~450 km",
    description: "Explore the royal heritage of Mysore and the picturesque hill station of Ooty.",
    gradient: "from-rose-500/10 to-red-400/10",
    iconColor: "text-rose-500",
    rating: 4.8,
    image: "/images/packages/mysore.png",
  },
];

const PackagesSection = () => {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId: number;
    let scrollPosition = 0;
    const scrollSpeed = 0.5; // pixels per frame

    const animate = () => {
      scrollPosition += scrollSpeed;

      // Calculate the width of one set of packages (including gaps)
      // Each package is 300px (or 350px on md) + 16px gap (or 24px on md)
      const cardWidth = window.innerWidth >= 768 ? 374 : 316; // card + gap
      const containerWidth = cardWidth * packages.length;

      // Reset position when we've scrolled through one complete set
      if (scrollPosition >= containerWidth) {
        scrollPosition = 0;
      }

      scrollContainer.style.transform = `translateX(-${scrollPosition}px)`;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  return (
    <section className="py-16 md:py-24 bg-background overflow-hidden relative">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-primary/10 rounded-full blur-[80px] md:blur-[100px]" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-purple-500/10 rounded-full blur-[80px] md:blur-[100px]" />
      </div>

      <div className="container px-4 mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16 space-y-3 md:space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-slate-800/50 border border-slate-700 text-slate-300 text-xs md:text-sm font-medium backdrop-blur-sm">
            <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-yellow-500" />
            <span>Premium Travel Packages</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-tight">
            Explore Kerala <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">&amp; Beyond</span>
          </h2>
          <p className="text-base md:text-lg text-slate-400 max-w-lg mx-auto">
            Handpicked destinations and curated itineraries for an unforgettable journey.
          </p>
        </div>

        {/* Continuous Scrolling Carousel */}
        <div className="relative max-w-7xl mx-auto overflow-hidden">
          <div ref={scrollRef} className="flex gap-4 md:gap-6 will-change-transform">
            {/* Render packages three times for truly seamless looping */}
            {[...packages, ...packages, ...packages].map((pkg, index) => (
              <div
                key={`${pkg.id}-${index}`}
                className="flex-shrink-0 w-[300px] md:w-[350px]"
              >
                <Card className="h-full border-slate-800 bg-slate-900/60 backdrop-blur-md shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-300 group overflow-hidden">
                  <CardContent className="p-0">
                    {/* Image Section */}
                    <div className="relative h-40 md:h-52 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent z-10" />
                      <img
                        src={pkg.image}
                        alt={pkg.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />

                      {/* Rating Badge */}
                      <div className="absolute top-3 right-3 bg-slate-900/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1 z-20">
                        <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                        <span className="text-xs font-semibold text-white">{pkg.rating}</span>
                      </div>

                      {/* Car Icon Badge */}
                      <div className="absolute top-3 left-3 bg-primary/90 backdrop-blur-sm p-1.5 rounded-full z-20">
                        <Car className="w-3 h-3 md:w-4 md:h-4 text-white" />
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-4 md:p-5 space-y-3">
                      <div>
                        <h3 className="text-lg md:text-xl font-bold text-white mb-1 group-hover:text-primary transition-colors">
                          {pkg.name}
                        </h3>
                        <p className="text-xs md:text-sm text-slate-400 line-clamp-2">
                          {pkg.description}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 text-slate-300">
                        <Clock className="w-4 h-4 text-primary" />
                        <span className="text-xs md:text-sm font-medium">{pkg.duration}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* View All Packages Button */}
        <div className="flex justify-center mt-12 md:mt-16">
          <Link to="/packages">
            <Button
              size="lg"
              className="group bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 text-white shadow-lg hover:shadow-primary/50 transition-all duration-300 px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
            >
              View All Packages
              <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PackagesSection;
